import { Fade, Slide, Grow, Collapse } from '@mui/material';
import { useTrail, animated } from 'react-spring';
import React, { memo, ReactNode } from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { mediaMax } from 'presentations/views/styles';

interface Props {
  children?: any;
  name: 'grow' | 'fade' | 'collapse' | 'slide';
  in?: boolean;
  timeout?: number;
  delay?: string;
  direction?: 'right' | 'down' | 'up';
}

interface TrailProps {
  isOpen: boolean;
  isSkip?: boolean;
  children: ReactNode;
  delay?: number;
  direction?: 'right' | 'bottom';
  isMobileFillWidth?: boolean;
  friction?: number;
  tension?: number;
  mass?: number;
  width?: number;
}

export const Trail = memo(
  ({ isOpen, isSkip, children, delay, direction, ...props }: TrailProps) => {
    if (isSkip) {
      return <>{children}</>;
    }
    const items = React.Children.toArray(children);
    const trail = useTrail(items.length, {
      config: {
        mass: props.mass || 5,
        tension: props.tension || 2000,
        friction: props.friction || 200,
        delay,
      },
      opacity: isOpen ? 1 : 0,
      x: isOpen ? 0 : props.width || 20,
      from: { opacity: 0, x: props.width || 20 },
    });
    return (
      <TrailsMain className="trails-main" {...props}>
        <div>
          {trail.map(({ x, ...rest }: { x: any }, index) => {
            const transform = x.interpolate((x: any) => {
              if (direction === 'right') {
                return `translate3d(${x}px, 0, 0)`;
              } else {
                return `translate3d(0,${x}px,0)`;
              }
            });

            const item: any = items[index];
            const childStyle = item?.props?.style;
            const style: any = {
              ...childStyle,
              ...rest,
              transform,
            };
            if (delay) {
              style['transitionDelay'] = isOpen ? delay : '0ms';
            }
            return (
              <animated.div key={index} className="trails-text" style={style}>
                <animated.div>{items[index]}</animated.div>
              </animated.div>
            );
          })}
        </div>
      </TrailsMain>
    );
  },
);

export default function Component(props: Props) {
  const style: any = {};
  if (props.delay) {
    style['transitionDelay'] = props.in ? props.delay : '0ms';
    style['transformOrigin'] = '0 0 0';
  }
  if (props.name === 'fade') {
    return (
      <Fade
        style={style}
        in={props.in === true || false}
        timeout={props.timeout}
      >
        {props.children}
      </Fade>
    );
  }
  if (props.name === 'grow') {
    return (
      <Grow
        style={style}
        in={props.in === true || false}
        timeout={props.timeout}
      >
        {props.children}
      </Grow>
    );
  }
  if (props.name === 'slide') {
    return (
      <Slide
        style={style}
        in={props.in === true || false}
        timeout={props.timeout}
        direction={props.direction}
      >
        {props.children}
      </Slide>
    );
  }
  if (props.name === 'collapse') {
    return (
      <Collapse
        style={style}
        in={props.in === true || false}
        timeout={props.timeout}
      >
        {props.children}
      </Collapse>
    );
  }
  return props.children;
}

const TrailsMain = styled.div<{ isMobileFillWidth?: boolean }>`
  ${({ isMobileFillWidth }) =>
    isMobileFillWidth &&
    css`
      ${mediaMax.tablet} {
        width: 100%;
      }
    `}
`;
